import { useEffect } from "react";

import { FullscreenLoading } from "@/components/FullscreenLoading";
import { ACCOUNT_DELETION_PATH, ServerUserProfile } from "@/constants";
import { ensureSelectedCountry } from "@/utils/countryStorage";
import {
  getAccessTokenByUrl,
  getExpiresInByUrl,
  getIdTokenByUrl,
  getNonceByUrl,
  getStoredSessionRedirectPath,
  getUserInformation,
  setupClient,
} from "@/utils/authentication";

type LoginCallbackHistory = {
  location: {
    hash: string;
  };
  push: (path: string) => void;
  replace?: (path: string) => void;
};

type UserCountry = {
  code?: string;
  [key: string]: unknown;
};

type UserInformation = {
  user_profile: string;
  representative?: string | number | null;
  wfp_manager?: string | number | null;
  organization?: string | number | null;
  country?: UserCountry | null;
  countries?: UserCountry[];
};

type LoginCallbackProps = {
  history: LoginCallbackHistory;
};

const LOGIN_FORBIDDEN_GUEST_PATH = "/loginForbiddenGuest";
const LOGIN_PATH = "/login";

const setOptionalLocalStorageItem = (key: string, value?: string | number | null) => {
  if (value === undefined || value === null || value === "") {
    localStorage.removeItem(key);
    return;
  }

  localStorage.setItem(key, String(value));
};

const replaceHistoryPath = (history: LoginCallbackHistory, path: string) => {
  if (history.replace) {
    history.replace(path);
    return;
  }

  history.push(path);
};

const consumePostLoginRedirect = () => {
  const postLoginRedirect = localStorage.getItem("post_login_redirect");

  if (postLoginRedirect) {
    localStorage.removeItem("post_login_redirect");
  }

  return postLoginRedirect;
};

const storeUserInformation = (data: UserInformation) => {
  localStorage.setItem("user_profile", data.user_profile);
  setOptionalLocalStorageItem("wfp_manger_id", data.wfp_manager);
  setOptionalLocalStorageItem("trader_representative_id", data.representative);
  setOptionalLocalStorageItem("trader_organization_id", data.organization);

  if (data.user_profile === ServerUserProfile.WFP_MANAGER) {
    const countries = data.countries ?? [];

    localStorage.setItem("countries", JSON.stringify(countries));
    ensureSelectedCountry(countries);
    return;
  }

  setOptionalLocalStorageItem("country_code", data.country?.code);
  localStorage.setItem("countries", JSON.stringify(data.country ? [data.country] : []));
};

const getLoginCallbackRedirectPath = (data: UserInformation) => {
  const postLoginRedirect = consumePostLoginRedirect();

  if (postLoginRedirect === ACCOUNT_DELETION_PATH) {
    return postLoginRedirect;
  }

  if (data.user_profile === ServerUserProfile.GUEST) {
    return LOGIN_FORBIDDEN_GUEST_PATH;
  }

  if (data.user_profile !== ServerUserProfile.WFP_MANAGER && postLoginRedirect) {
    return postLoginRedirect;
  }

  return getStoredSessionRedirectPath() ?? LOGIN_PATH;
};

// A page displayed while CIAM returns control to the app.
export const LoginCallback = ({ history }: LoginCallbackProps) => {
  useEffect(() => {
    try {
      const callbackHash = history.location.hash;
      const nonce = localStorage.getItem("nonce");
      const receivedNonce = getNonceByUrl(callbackHash);
      const ciamIdToken = getIdTokenByUrl(callbackHash);
      const ciamAccessToken = getAccessTokenByUrl(callbackHash);
      const expiresIn = Number.parseInt(getExpiresInByUrl(callbackHash), 10);

      if (!ciamAccessToken || receivedNonce !== nonce) {
        replaceHistoryPath(history, LOGIN_PATH);
        return;
      }

      localStorage.setItem("ciam_id_token", ciamIdToken);
      localStorage.setItem("ciam_access_token", ciamAccessToken);

      const expiryDate = new Date();
      expiryDate.setSeconds(expiryDate.getSeconds() + expiresIn);
      localStorage.setItem("expiry_date", expiryDate.toISOString());

      setupClient(ciamAccessToken);
      localStorage.removeItem("nonce");

      getUserInformation()
        .then((data: UserInformation) => {
          storeUserInformation(data);
          replaceHistoryPath(history, getLoginCallbackRedirectPath(data));
        })
        .catch(error => {
          console.log(error);
          replaceHistoryPath(history, LOGIN_PATH);
        });
    } catch (error) {
      console.log(error);
      replaceHistoryPath(history, LOGIN_PATH);
    }
  }, [history]);

  return <FullscreenLoading isVisible />;
};
